import React, { useContext } from 'react'

import PlannedCruiseCtaContent from './PlannedCruiseCtaContent'
import { ItemCardVariant } from '../ItemCard'
import ItineraryItemCard from '../ItineraryItemCard'
import { PlannedItemPriceContent } from '../PlannedItemPriceContent'

import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import { isLEOffer, isCruiseV1Offer } from 'lib/offer/offerTypes'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import BookmarkLocation from 'tripPlanner/components/Bookmark/BookmarkCardNew/BookmarkLocation'
import LEOfferPriceDetails from 'tripPlanner/components/ItemOfferPrice/LEOfferPriceDetails'
import SavedOfferCard from 'tripPlanner/components/SavedOffers/SavedOfferCard'
import TripSummaryCardLoadingSkeleton from 'tripPlanner/components/TripSummary/TripSummaryCardLoadingSkeleton'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useOffer } from 'tripPlanner/hooks/api'
import useTourV1ItemConfirmationCodes from 'tripPlanner/hooks/confirmationCodes/useTourV1ItemConfirmationCodes'
import { CruiseItem, LETripItem } from 'tripPlanner/types/tripItem'
import {
  buildMyEscapesOrderLink,
  formatDatesAndDays,
  formatStartAndEndLocation,
} from 'tripPlanner/utils'
import { buildProductLink } from 'tripPlanner/utils/bookmark/restoreCart'

interface Props {
  item: Extract<LETripItem, CruiseItem>
  onClickCard: (offerLink: string) => void
  onClickTripName?: () => void
  variant: ItemCardVariant
}

export default function CruiseV1ItemCard({
  item,
  onClickCard,
  onClickTripName,
  variant,
}: Props) {
  const { data, isLoading } = useOffer(item.code)

  const tripContext = useContext(TripContext)
  const viewAsTemplate = tripContext?.viewAsTemplate
  const canEdit = tripContext?.permissions.canEdit ?? false

  const timeFormat = useRegionTimeFormat()

  const bookingId = useTourV1ItemConfirmationCodes(item)

  if (isLoading) return <TripSummaryCardLoadingSkeleton />

  if (
    !data ||
    !isCruiseV1Offer(data) ||
    item.savedItemData.offerType !== 'tour'
  ) {
    return null
  }

  const offer = data as App.Offer

  const isOfferAvailable = isLEOffer(offer)
    ? scheduleIsCurrent(offer?.onlinePurchaseSchedule)
    : true
  if (item.isBooked) {
    const myEscapesLink =
      canEdit && !viewAsTemplate ? buildMyEscapesOrderLink(item) : undefined
    return (
      <ItineraryItemCard
        item={item}
        name={item.name}
        locationDescription={formatStartAndEndLocation(item)}
        descriptors={[formatDatesAndDays(item, timeFormat)]}
        confirmationCode={bookingId.codeString}
        confirmationCodeLabel={bookingId.label}
        onClickTripName={onClickTripName}
        variant={variant}
        onClick={myEscapesLink ? () => onClickCard(myEscapesLink) : undefined}
      />
    )
  }

  const tileLink = buildProductLink(item, !viewAsTemplate, { offer }) ?? ''
  const { discountPercent, currencyCode, price, priceDisplayType } =
    item.savedItemData

  let priceAreaContent: JSX.Element | null = null
  if (viewAsTemplate) {
    const packageId =
      'packageId' in item.savedItemData
        ? item.savedItemData.packageId
        : undefined
    priceAreaContent = (
      <LEOfferPriceDetails
        offer={offer}
        package={offer.packages.find((p) => p.id === packageId)}
      />
    )
  } else {
    priceAreaContent = (
      <PlannedItemPriceContent
        currencyCode={currencyCode}
        price={price}
        discountPercent={discountPercent}
        isOfferAvailable={isOfferAvailable}
        priceDisplayType={priceDisplayType}
      />
    )
  }

  const ctaAreaContent = (
    <PlannedCruiseCtaContent
      item={item}
      isOfferAvailable={isOfferAvailable}
      variant={variant}
    />
  )

  const locationDescription = (
    <BookmarkLocation
      variant="single"
      startLocation={item.startPlace.name}
      showIcon={false}
    />
  )

  return (
    <SavedOfferCard
      item={item}
      name={item.name}
      locationDescription={locationDescription}
      priceAreaContent={priceAreaContent}
      ctaAreaContent={ctaAreaContent}
      isSoldOut={!isOfferAvailable}
      testId="saved-cruise-card"
      onClick={() => onClickCard(tileLink)}
      onClickTripName={onClickTripName}
      variant={variant}
    />
  )
}
